import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { CategoryBehaviour } from '../utils/api';
import { BarChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, fmtMonth } from '../utils/theme';
import { POSTCODE_NAMES } from '../utils/postcodes';

const OVER_COLOR      = COLORS.teal;   // over-indexes vs national
const UNDER_COLOR     = '#E5484D';     // under-indexes vs national
const BAR_COLOR       = COLORS.blue;
const HIGHLIGHT_COLOR = COLORS.teal;
const TOP_N = 15;

const pctFmt  = v => (v < 1 && v !== 0 ? `${(+v).toFixed(2)}%` : `${(+v).toFixed(1)}%`);
const idxFmt  = v => `${Math.round(v)}`;
const geoName = code => (POSTCODE_NAMES[code] ? `${POSTCODE_NAMES[code]} (${code})` : code);

// Geo dropdown: National + every postcode area, sorted by place name.
const GEO_OPTIONS = [
  { value: '', label: 'National (all UK)' },
  ...Object.entries(POSTCODE_NAMES)
    .map(([code, name]) => ({ value: code, label: `${name} (${code})` }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

// One horizontal bar chart of index vs national (100 = national average) for a
// set of geos, with a dashed reference line at 100.
function GeoIndexChart({ title, sub, rows, color }) {
  const categories = rows.map(r => geoName(r.segment));
  const series = [{ name: 'Index vs national', data: rows.map(r => Math.round(r.index)), color }];
  return (
    <ChartCard title={title} sub={sub}>
      {rows.length === 0
        ? <div className="empty-state">No data.</div>
        : (
          <BarChart
            categories={categories}
            series={series}
            height={Math.max(260, rows.length * 26 + 60)}
            horizontal
            markLines={[100]}
            xFormatter={idxFmt}
            tooltipFormatter={v => `${Math.round(v)} (100 = national)`}
          />
        )}
    </ChartCard>
  );
}

export default function CategoryComparisonProvincesView() {
  const { category: globalCategory } = useCategory();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();
  const { month } = useMonth();

  const [sessionType, setSessionType] = useState('open_app');
  const [geo,         setGeo]         = useState('');

  const selectedMonth = month || latestMonth;

  // ── Selected category: penetration by province (+ national reference) ──────
  const byGeo = useApi(
    () => CategoryBehaviour.penetrationByGeo({
      category: globalCategory, session_type: sessionType, month_id: selectedMonth,
    }),
    { globalCategory, sessionType, selectedMonth },
    !!globalCategory && !!selectedMonth,
  );

  const national = byGeo.data?.[0]?.national_penetration_pct ?? null;
  const { over, under } = useMemo(() => {
    if (!byGeo.data?.length || !national) return { over: [], under: [] };
    // Index vs national: 100 = national average.
    const ranked = byGeo.data
      .map(r => ({ ...r, index: ((r.penetration_pct ?? 0) / national) * 100 }))
      .sort((a, b) => b.index - a.index);
    return {
      over:  ranked.slice(0, TOP_N),
      under: ranked.slice(-TOP_N).reverse(),   // most-below-national first
    };
  }, [byGeo.data, national]);

  // ── All categories within a chosen province (or National) ─────────────────
  const compare = useApi(
    () => CategoryBehaviour.penetrationCompare({
      month_id: selectedMonth, session_type: sessionType, geo: geo || undefined,
    }),
    { selectedMonth, sessionType, geo },
    !!selectedMonth,
  );

  const geoLabel = geo ? (POSTCODE_NAMES[geo] || geo) : 'National (all UK)';
  const { compareCats, compareSeries } = useMemo(() => {
    if (!compare.data?.length) return { compareCats: [], compareSeries: [] };
    const compareCats = compare.data.map(r => r.category);
    const values = compare.data.map(r => {
      const v = +(r.penetration_pct ?? 0);
      return r.category === globalCategory
        ? { value: v, itemStyle: { color: HIGHLIGHT_COLOR, borderRadius: [0, 3, 3, 0] } }
        : v;
    });
    return { compareCats, compareSeries: [{ name: 'Penetration %', data: values, color: BAR_COLOR }] };
  }, [compare.data, globalCategory]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  const catRef = globalCategory
    ? `${globalCategory} · index vs national (100 = national ${national != null ? pctFmt(national) : '—'})`
    : '';

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ── Where the selected category over / under-indexes ─────────────── */}
      {!globalCategory ? (
        <div className="empty-state" style={{ marginBottom: 16 }}>
          Select a category to see the provinces where it over- and under-indexes vs national.
        </div>
      ) : byGeo.loading ? (
        <ChartLoader height={300} />
      ) : byGeo.error ? (
        <ChartError message={byGeo.error} height={300} />
      ) : (
        <div className="dj-charts-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <GeoIndexChart
            title={`Top ${TOP_N} provinces — over-indexing`}
            sub={catRef}
            rows={over}
            color={OVER_COLOR}
          />
          <GeoIndexChart
            title={`Top ${TOP_N} provinces — under-indexing`}
            sub={catRef}
            rows={under}
            color={UNDER_COLOR}
          />
        </div>
      )}

      {/* ── All categories within a chosen province ─────────────────────── */}
      <div className="filters-bar">
        <span className="filter-label">Geography</span>
        <select className="filter-select" value={geo} onChange={e => setGeo(e.target.value)}>
          {GEO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <ChartCard
        title={`Category Penetration — ${geoLabel}`}
        sub={`${fmtMonth(selectedMonth)} · ${sessionType} · % of ${geo ? 'local' : 'UK'} panel${globalCategory ? ' · selected category in teal' : ''}`}
      >
        {compare.loading  && <ChartLoader height={420} />}
        {compare.error    && <ChartError message={compare.error} height={420} />}
        {!compare.loading && !compare.error && compareCats.length > 0 && (
          <BarChart
            categories={compareCats}
            series={compareSeries}
            height={Math.max(360, compareCats.length * 22 + 60)}
            horizontal
            xFormatter={pctFmt}
            tooltipFormatter={pctFmt}
          />
        )}
        {!compare.loading && !compare.error && compareCats.length === 0 && (
          <div className="empty-state">No penetration data for {geoLabel} in {fmtMonth(selectedMonth)}.</div>
        )}
      </ChartCard>
    </div>
  );
}
